/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/alt-text */

'use client';

import { useCallback } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { favouritesAtom } from '@core/Atoms/Favourites/Favourites.atom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import { Header } from '@gds/Header/Header';
import type { HeaderTrackingObj } from '@gds/Header/Header.types';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { accModalAtomFamily } from '@my-account/Atoms/AccModal.atom';
import type { User } from '@my-account/Entities/Account.entity';
import { nlsToggleAtom } from '@natural-language-search/Atoms/NaturalLanguageSearchToggle.atom';
import { NaturalLanguageSearch } from '@natural-language-search/NaturalLanguageSearch';
import { validateUrl } from '@search/Utils/ValidateUrl';
import { ctaClickedToEvent } from '@tracking/Schemas/CtaClicked/CtaClickedToEvent';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import type { NavigationItem } from '@umbraco/GetUmbracoNavigation';

import type { HeaderLinkProps, NavItem } from '../../Entities/Nav.entity';
import { sanitizeNavigationItemsForHeader } from '../../Utils/SanitizeNavigationItemsForHeader';
import { headerLogoMap } from './HeaderLogoMap';
import type { HeaderNavigationMessages } from './HeaderNavigation.messages';

import styles from './HeaderNavigation.module.css';

interface HeaderNavigationProps {
  strings: Record<keyof typeof HeaderNavigationMessages, string>;
  navigationItems: NavigationItem[];
  tenant?: Tenant;
  isSearchEnabled?: boolean;
  userProfile?: User | null;
}

const trackingFunction = ({ category, label, href }: HeaderTrackingObj) => {
  trackCustomEvent({
    event: ctaClickedToEvent({ ctaType: category, ctaWording: label, targetUrl: href || '' }),
    context: [],
  });
};

export const HeaderNavigation = ({
  strings,
  navigationItems,
  tenant,
  isSearchEnabled,
  userProfile,
}: HeaderNavigationProps) => {
  const favCount = useAtomValue(favouritesAtom);
  const toggleLoginModal = useSetAtom(accModalAtomFamily('login'));

  const { push } = useRouter();
  const flags = useAtomValue(flagsAtom);
  const toggleSearchBar = useSetAtom(nlsToggleAtom);

  const navigationMessages = {
    closeSearchLabel: strings.closeSearchLabel,
    logoLabel: strings.logoLabel,
    langListHeading: strings.langListHeading,
    burgerMenuButtonLabel: strings.burgerMenuButtonLabel,
    favoritesCountLabel: strings.favoritesCountLabel,
    showAllLabel: strings.showAllLabel,
    spaceBarNotification: strings.spaceBarNotification,
    subMenuLabel: strings.subMenuLabel,
    auxTelLabel: strings.auxTelLabel,
    auxEmailLabel: strings.auxEmailLabel,
    auxAppHeading: strings.auxAppHeading,
  };
  const baseUrl = `/`;

  const favItems = {
    favoritesNumber: favCount?.length ?? 0,
    label: strings.favouitesLabel,
    href: `${baseUrl}favourites`,
  };

  const SearchBar = useCallback(
    () => <NaturalLanguageSearch validateUrl={validateUrl} strings={strings} />,
    [isSearchEnabled],
  );

  const customLink = ({ children, ...props }: HeaderLinkProps) => (
    <Link
      {...props}
      href={props.href || '#'}
      data-special-prop="special"
      prefetch={false}
      aria-label="menu header"
    >
      {children}
    </Link>
  );

  const LogoElement = tenant ? headerLogoMap[tenant] : undefined;

  return (
    <div className={styles.wrapper}>
      <div className={styles.top}>
        <Header
          logoHref={`${baseUrl}`}
          dataTestId="intl-web-app-header"
          navigation={sanitizeNavigationItemsForHeader(navigationItems) as NavItem[]}
          locale={navigationMessages}
          LinkComponent={customLink}
          langItemConfig={undefined}
          favoritesItemConfig={favItems}
          LogoElement={LogoElement}
          trackingFn={trackingFunction}
          accountItemConfig={{
            hide: !flags.myAccount,
            label: userProfile ? strings.myAccountLabel : strings.loginLabel,
            onClick: userProfile ? () => push?.('/account') : () => toggleLoginModal?.(),
            dataTestId: `${userProfile ? 'my-account' : 'login'}-button`,
          }}
          SearchBar={isSearchEnabled ? SearchBar : undefined}
          toggleSearchBar={toggleSearchBar}
        />
      </div>
    </div>
  );
};
